import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Stack, Chip, MenuItem,
  CircularProgress, Alert, IconButton, Tooltip, FormControlLabel, Switch, Autocomplete,
} from '@mui/material';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { startActivity } from '../../utils/activityTracker';

import { datumsUitTekst } from '../../utils/datumsUitTekst';

const isoDaysFromNow = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtWeek = (iso) => {
  const d = new Date(iso); const end = new Date(d); end.setDate(end.getDate() + 4);
  const opt = { day: 'numeric', month: 'short' };
  return `${d.toLocaleDateString('nl-NL', opt)}–${end.toLocaleDateString('nl-NL', opt)}`;
};
const COMMON_FUNCTIONS = ['Camera', 'Regie', 'Geluid', 'Editor', 'Animator', 'Producer', 'Grip', 'Licht'];
// Airtable multipleSelects: function kan array of string zijn — altijd normaliseren.
const fnStr = (p) => {
  const raw = p?.function || p?.Functie || '';
  return Array.isArray(raw) ? raw.join(', ') : String(raw || '');
};

export default function DraaidagPlanner() {
  const [roles, setRoles] = useState([{ role: 'Camera', prefer: [] }, { role: 'Regie', prefer: [] }]);
  const [needHeavy, setNeedHeavy] = useState(false);
  const [startDate, setStartDate] = useState(isoDaysFromNow(0));
  const [endDate, setEndDate] = useState(isoDaysFromNow(42));
  // Losse klant-opties (bv. 25 sep / 9 okt / 5 nov) naast elkaar leggen (Raijmond, 24 aug):
  // per datum één analyse, samengevoegd tot één resultaat.
  const [losseDatums, setLosseDatums] = useState([]);
  const [nieuweDatum, setNieuweDatum] = useState('');
  const voegDatumToe = () => {
    if (!nieuweDatum || losseDatums.includes(nieuweDatum)) return;
    setLosseDatums((prev) => [...prev, nieuweDatum].sort()); setNieuweDatum('');
  };
  const [plakTekst, setPlakTekst] = useState('');
  const herkenDatums = () => {
    const nieuw = datumsUitTekst(plakTekst);
    if (!nieuw.length) { setError('Geen datums herkend in de tekst — probeer "25 september" of "25-9".'); return; }
    setError(null);
    setLosseDatums((prev) => [...new Set([...prev, ...nieuw])].sort());
    setPlakTekst('');
  };
  const [offerNumber, setOfferNumber] = useState('');
  // Zoeken op offerte/opdracht i.p.v. een nummer uit je hoofd typen (wiki-feedback, 9 sep).
  const [koppelOpties, setKoppelOpties] = useState([]);
  const [koppelZoek, setKoppelZoek] = useState('');
  const [koppelKeuze, setKoppelKeuze] = useState(null);
  useEffect(() => {
    const q = koppelZoek.trim();
    if (q.length < 2) { setKoppelOpties([]); return undefined; }
    const timer = setTimeout(() => {
      authFetch(`${BACKEND_URL}/Api/koppel-zoek?q=${encodeURIComponent(q)}`)
        .then((r) => leesJson(r, 'Zoeken'))
        .then((d) => setKoppelOpties(d.opties || []))
        .catch(() => setKoppelOpties([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [koppelZoek]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [persons, setPersons] = useState([]);

  // Personen voor de voorkeursnamen-dropdown (gefilterd op de gekozen functie).
  useEffect(() => {
    authFetch(`${BACKEND_URL}/Api/planner-todo/persons`).then(r => leesJson(r, 'Personen laden'))
      .then(d => setPersons(d.persons || [])).catch(e => setError(e.message));
  }, []);
  const personsForRole = (role) => {
    const rl = String(role || '').toLowerCase();
    if (!rl) return persons;
    const matched = persons.filter(p => fnStr(p).toLowerCase().includes(rl));
    return matched.length ? matched : persons;   // geen functie-match → iedereen tonen
  };

  const setRole = (i, k, v) => setRoles(rs => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRole = () => setRoles(rs => [...rs, { role: '', prefer: [] }]);
  const removeRole = (i) => setRoles(rs => rs.filter((_, idx) => idx !== i));

  const search = async () => {
    setLoading(true); setError(null); setResult(null);
    // De zoektocht loopt alle agenda's van de kandidaten na en kan een minuut
    // duren — via de activiteiten-tracker krijg je een (browser)notificatie
    // zodra de analyse klaar is, ook als je ondertussen ergens anders zit.
    const act = startActivity('Draaidag-planner: weken zoeken', { path: '/draaidag-planner' });
    try {
      const rollen = roles.filter(r => r.role.trim()).map(r => ({
        role: r.role.trim(),
        preferNames: (r.prefer || []).map(s => (typeof s === 'string' ? s : s?.name || '')).filter(Boolean),
      }));
      const vraag = async (van, tot) => {
        const r = await authFetch(`${BACKEND_URL}/Api/draaidag-planner`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roles: rollen, startDate: van, endDate: tot, offerNumber: offerNumber.trim() || undefined, needHeavy }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Zoeken mislukt');
        return d;
      };
      let data;
      if (losseDatums.length) {
        // Per losse datum een analyse; weken en persoonsdata samenvoegen zodat de
        // opties naast elkaar staan i.p.v. één lange periode.
        const delen = [];
        for (const d of losseDatums) delen.push(await vraag(d, d)); // sequentieel: Graph knijpt parallel af
        data = { ...delen[0], weeks: [], roles: [], losseDatums };
        const rolMap = new Map();
        delen.forEach((deel) => {
          (deel.weeks || []).forEach((w) => { if (!data.weeks.includes(w)) data.weeks.push(w); });
          (deel.roles || []).forEach((g) => {
            const rol = rolMap.get(g.role) || { ...g, people: [] };
            (g.people || []).forEach((p) => {
              const bestaand = rol.people.find((x) => (x.name || x.id) === (p.name || p.id));
              if (bestaand) bestaand.weeks = { ...(bestaand.weeks || {}), ...(p.weeks || {}) };
              else rol.people.push({ ...p, weeks: { ...(p.weeks || {}) } });
            });
            rolMap.set(g.role, rol);
          });
        });
        data.roles = [...rolMap.values()];
        data.note = `Losse datums vergeleken: ${losseDatums.map((d) => new Date(`${d}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })).join(' · ')}${data.note ? ` — ${data.note}` : ''}`;
      } else {
        data = await vraag(startDate, endDate);
      }
      setResult(data);
      act.done(`${(data.weeks || []).length} weken geanalyseerd`);
    } catch (e) { setError(e.message); act.fail(e.message); } finally { setLoading(false); }
  };

  // Per week per rol: wie kan, wie niet (mét reden) — de samenvatting van de zoektocht.
  const availField = needHeavy ? 'available_heavy' : 'available';
  const weekView = (week) => {
    const perRole = (result.roles || []).map(g => {
      const avail = [];
      const blocked = [];
      (g.people || []).forEach(p => {
        const w = p.weeks?.[week];
        if (!w) { blocked.push({ p, reden: 'geen agenda-data' }); return; }
        if (w[availField]) {
          // Konden we zijn agenda niet lezen, dan is 'vrij' een aanname en geen feit
          // (Joris, 11 sep — 403 op een mailbox gaf stil 'helemaal vrij').
          avail.push(w.agenda_gelezen === false ? { ...p, agendaOnbekend: true } : p);
          return;
        }
        // Mét de reden erbij: welke draaidagen en welke drukke dagen (wiki-feedback, 9 sep).
        const namen = (w.draaidag_namen || []).join(', ');
        const druk = (w.drukke_dagen || []).join(', ');
        const afwezig = (w.afwezig_dagen || []).join(', ');
        let reden = 'vol';
        if ((w.afwezig ?? 0) >= 5 || ((w.free_days ?? 1) <= 0 && afwezig)) reden = `afwezig${afwezig ? `: ${afwezig}` : ''}`;
        else if ((w.remaining ?? 0) <= 0) reden = `max draaidagen bereikt (${w.draaidagen}/${w.max})${namen ? `: ${namen}` : ''}`;
        else if (needHeavy && w.available && !w.available_heavy) reden = `zware limiet bereikt (${w.heavy}/${w.max_heavy} M/L/XL)${namen ? `: ${namen}` : ''}`;
        else if ((w.free_days ?? 1) <= 0) reden = `geen vrije dag over${namen ? ` — draaidagen: ${namen}` : ''}${druk ? ` — agenda: ${druk}` : ''}`;
        else if (w.busy) reden = `agenda vol${druk ? `: ${druk}` : ''}`;
        blocked.push({ p, reden });
      });
      return { role: g.role, avail, blocked, none: (g.people || []).length === 0 };
    });
    return { perRole, possible: perRole.length > 0 && perRole.every(pr => pr.avail.length > 0) };
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <MovieFilterIcon sx={{ color: '#7c3aed' }} />
        <Typography variant="h5" sx={{ fontWeight: 800, flex: 1 }}>Draaidag-planner</Typography>
        <Tooltip title="Max draaidagen per week per persoon instellen (Admin → Planning-regels)">
          <IconButton component="a" href="/admin/settings?tab=app&section=planning-regels"><SettingsIcon /></IconButton>
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Vind weken waarin je gewenste crew (welke functies je maar wilt) samen kan draaien — met de max
        draaidagen per persoon (totaal + zwaar M/L/XL), hun agenda en de voorbereidingstijd uit de offerte.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 1 }}>Gewenste crew (functies)</Typography>
        <Stack spacing={1}>
          {roles.map((r, i) => (
            <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <TextField select size="small" label="Functie" value={r.role} onChange={(e) => setRole(i, 'role', e.target.value)} sx={{ width: 170 }}>
                {COMMON_FUNCTIONS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                {!COMMON_FUNCTIONS.includes(r.role) && r.role && <MenuItem value={r.role}>{r.role}</MenuItem>}
              </TextField>
              <Autocomplete
                multiple size="small" sx={{ flex: 1 }}
                options={personsForRole(r.role)}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o?.name || '')}
                isOptionEqualToValue={(o, v) => (o?.id || o) === (v?.id || v)}
                value={r.prefer}
                onChange={(_e, v) => setRole(i, 'prefer', v)}
                renderInput={(params) => <TextField {...params} label="Voorkeurspersonen (optioneel)"
                  placeholder={r.prefer.length ? '' : `Iedereen met functie ${r.role || '…'}`} />}
              />
              <IconButton size="small" onClick={() => removeRole(i)} disabled={roles.length <= 1}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addRole} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>Functie toevoegen</Button>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-end' }} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <TextField type="date" label="Vanaf" size="small" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <TextField type="date" label="Tot" size="small" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={losseDatums.length > 0} />
          <Autocomplete size="small" sx={{ minWidth: 300 }} options={koppelOpties} value={koppelKeuze}
            getOptionLabel={(o) => o?.label || ''} isOptionEqualToValue={(a, b) => a?.id === b?.id}
            filterOptions={(x) => x} inputValue={koppelZoek} onInputChange={(_, v) => setKoppelZoek(v)}
            onChange={(_, v) => {
              setKoppelKeuze(v);
              // Een offerte geeft de voorbereidingstijd uit de offerteregels; een project/lead alleen de naam.
              setOfferNumber(v?.type === 'offerte' ? String(v.id).replace('offer-', '') : '');
            }}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box><Typography variant="body2">{o.label}</Typography>{o.sub && <Typography variant="caption" color="text.secondary">{o.sub}</Typography>}</Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Offerte of opdracht (optioneel)" placeholder="Zoek op nummer of klant…" />} />
          <FormControlLabel control={<Switch size="small" checked={needHeavy} onChange={(e) => setNeedHeavy(e.target.checked)} />}
            label={<Typography variant="caption">grote productie (M/L/XL)</Typography>} />
          <Button variant="contained" onClick={search} disabled={loading}
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700, height: 40, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
            {losseDatums.length ? `Vergelijk ${losseDatums.length} datum${losseDatums.length === 1 ? '' : 's'}` : 'Zoek weken'}
          </Button>
        </Stack>
        {/* Klantopties: één blok met uitleg — het losse plusje was niet te begrijpen (wiki-feedback, 9 sep). */}
        <Paper variant="outlined" sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: '#faf5ff', borderColor: '#e9d5ff' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#5b21b6' }}>Klantopties vergelijken</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Heeft de klant een paar datums genoemd? Plak de zin uit de mail, of voeg ze los toe. Dan zie je per datum naast elkaar wie er kan; de periode Vanaf/Tot wordt dan genegeerd.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <TextField size="small" multiline minRows={1} maxRows={4} fullWidth value={plakTekst} onChange={(e) => setPlakTekst(e.target.value)}
              placeholder='Bijv. "we kunnen vrijdag 25 september, 9 okt of 5 november"'
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); herkenDatums(); } }} />
            <Button size="small" variant="outlined" onClick={herkenDatums} disabled={!plakTekst.trim()} sx={{ textTransform: 'none', whiteSpace: 'nowrap', height: 40 }}>Datums herkennen</Button>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField type="date" size="small" InputLabelProps={{ shrink: true }} label="Of los" value={nieuweDatum}
                onChange={(e) => setNieuweDatum(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); voegDatumToe(); } }} sx={{ width: 170 }} />
              <Button size="small" startIcon={<AddIcon />} onClick={voegDatumToe} disabled={!nieuweDatum} sx={{ textTransform: 'none', height: 40 }}>Toevoegen</Button>
            </Stack>
          </Stack>
        </Paper>
        {losseDatums.length > 0 && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }} alignItems="center">
            <Typography variant="caption" color="text.secondary">Klant-opties:</Typography>
            {losseDatums.map((d) => (
              <Chip key={d} size="small" label={new Date(`${d}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
                onDelete={() => setLosseDatums((prev) => prev.filter((x) => x !== d))} sx={{ bgcolor: '#ede9fe', color: '#5b21b6', fontWeight: 600 }} />
            ))}
            <Typography variant="caption" color="text.secondary">— de periode Vanaf/Tot wordt dan genegeerd.</Typography>
          </Stack>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {result?.note && <Alert severity="info" sx={{ mb: 2 }}>{result.note}</Alert>}

      {result?.prep && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <b>Voorbereidingstijd:</b> {result.prep.prepHours != null ? `~${result.prep.prepHours}u (${Math.round(result.prep.prepHours / 8 * 10) / 10} dagen)` : 'onbekend'} — bron: {result.prep.source}.
        </Alert>
      )}

      {result && (result.weeks || []).length > 0 && (
        <Stack spacing={1}>
          {result.weeks.map((week) => {
            const { perRole, possible } = weekView(week);
            return (
              <Paper key={week} variant="outlined" sx={{ p: 1.5, borderLeft: `4px solid ${possible ? '#16a34a' : '#f59e0b'}` }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, flex: 1 }}>Week van {fmtWeek(week)}</Typography>
                  {possible
                    ? <Chip size="small" label={`✓ plek voor ${perRole.map(p => p.role).join(' + ')}`} sx={{ fontWeight: 700, bgcolor: '#dcfce7', color: '#166534' }} />
                    : <Chip size="small" label={`geen ${perRole.filter(p => !p.avail.length).map(p => p.role).join(', ')}`} sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />}
                </Stack>
                {/* Samenvatting van de zoektocht — óók als het niet kan: wie valt af en waarom */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                  {perRole.map(pr => (
                    <Box key={pr.role} sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: pr.avail.length ? '#166534' : '#92400e' }}>
                        {pr.role} {pr.avail.length ? `(${pr.avail.length} beschikbaar)` : '(niemand beschikbaar)'}
                      </Typography>
                      {pr.none && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Geen personen met deze functie gevonden.
                        </Typography>
                      )}
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                        {pr.avail.map(p => {
                          const w = p.weeks[week];
                          return (
                            <Tooltip key={p.name} title={`${w.draaidagen}/${w.max} draaidagen deze week · ${w.heavy}/${w.max_heavy} zwaar${(w.draaidag_namen || []).length ? ` — ${w.draaidag_namen.join(', ')}` : ''}${(w.drukke_dagen || []).length ? ` — agenda: ${w.drukke_dagen.join(', ')}` : ''}${(w.afwezig_dagen || []).length ? ` — afwezig: ${w.afwezig_dagen.join(', ')}` : ''}${(w.taken_dagen || []).length ? ` — taken die dan moeten schuiven: ${w.taken_dagen.join(' · ')}` : ''}${w.agenda_gelezen === false ? ' — LET OP: agenda niet gelezen, dit zegt niets over zijn beschikbaarheid' : ''}`}>
                              <Chip size="small"
                                label={`${p.name.split(' ')[0]} · ${w.remaining} vrij${w.afwezig ? ` · ${w.afwezig}d afwezig` : ''}${w.taken ? ` · ${w.taken} taken` : ''}${w.agenda_gelezen === false ? ' ⚠' : ''}`}
                                sx={{ bgcolor: w.agenda_gelezen === false ? '#fef3c7'
                                  : (p.preferred ? '#ede9fe' : '#dcfce7'),
                                  fontWeight: p.preferred ? 700 : 500 }} />
                            </Tooltip>
                          );
                        })}
                      </Stack>
                      {pr.blocked.length > 0 && (
                        <Box sx={{ mt: 0.25 }}>
                          {pr.blocked.map(({ p, reden }) => (
                            <Typography key={p.name} variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
                              ✕ {p.name.split(' ')[0]} — {reden}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Paper>
            );
          })}
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
            "N vrij" = resterende draaidag-slots die week (totaal). "d afwezig" = vrije dagen/vakantie (telt als bezet). "taken" = ander werk dat die week gepland staat en dan moet schuiven. "Grote productie" checkt ook de zware-draaidag-limiet (M/L/XL).
            Voorkeurspersonen paars. Voorstel; je plant zelf in.
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
